import { useMemo, useRef, useState } from "react";

// ============================================================
// OWNER-ONLY BUSINESS DOCUMENT TEMPLATES
// Sample text + placeholders. Edit text directly in the preview
// before printing; permanent edits can be made to this file.
// ============================================================

const templates = [
  {
    id: "proposal",
    title: "Project Proposal",
    category: "Pre-engagement",
    purpose: "Use to propose a piece of work before it is agreed.",
    sections: [
      {
        h: "Project overview",
        blocks: [
          { t: "fields", rows: [["Client", "[Client name]"], ["Project name", "[Working title]"], ["Prepared by", "Stephanie Madojemu — Savvy Stephanie Analytics & Consulting"], ["Date", "[Date]"]] },
          { t: "p", x: "[Two or three sentences describing the client's situation in their words, e.g. “Reporting is assembled manually each week from three systems and takes around a day to produce.”]" },
        ],
      },
      {
        h: "Understanding of the problem",
        blocks: [
          { t: "p", x: "[Short paragraph demonstrating that you understood the problem: what is slow, manual, unclear or unreliable today, and what it costs the business.]" },
        ],
      },
      {
        h: "Proposed approach",
        blocks: [
          { t: "ol", items: ["[Discussion] — agree goals, systems and access.", "[Analysis] — review data / map the current process.", "[Build] — prepare the deliverable with review checkpoints.", "[Review] — walk through drafts and agree refinements.", "[Handover] — deliver files, documentation and a walkthrough."] },
        ],
      },
      {
        h: "Scope",
        blocks: [
          { t: "ul", items: ["In scope: [deliverables and boundaries]", "Out of scope: [anything explicitly excluded, e.g. new software licences]"] },
        ],
      },
      {
        h: "Price and payment",
        blocks: [
          { t: "fields", rows: [["Price", "[Fixed price in USD or EUR]"], ["Payment terms", "[e.g. 50% on signature, 50% on delivery]"], ["Valid until", "[Date]"]] },
        ],
      },
      {
        h: "Assumptions and dependencies",
        blocks: [
          { t: "ul", items: ["[e.g. client provides data access by the start date]", "[e.g. one named contact available for a weekly check-in]"] },
        ],
      },
      { t: "sign" },
    ],
  },
  {
    id: "quotation",
    title: "Quotation",
    category: "Pre-engagement",
    purpose: "A simple fixed-price quote for an agreed scope.",
    sections: [
      {
        h: "Quotation details",
        blocks: [
          { t: "fields", rows: [["Quote number", "SS-Q-[###]"], ["Date", "[Date]"], ["Valid until", "[Date]"], ["Client", "[Client name and details]"]] },
        ],
      },
      {
        h: "Items",
        blocks: [
          { t: "table", head: ["Service", "Description", "Qty", "Rate", "Amount"], rows: [["[Service name]", "[One-line scope reminder]", "[1]", "[Rate]", "[Amount]"], ["", "", "", "Subtotal", "[Subtotal]"], ["", "", "", "Tax (status to confirm)", "[Tax]"], ["", "", "", "Total", "[Total]"]] },
        ],
      },
      {
        h: "Terms",
        blocks: [
          { t: "ul", items: ["Prices are in [USD / EUR] as agreed.", "Scope changes are agreed in writing before extra work continues.", "Required software licences are separate.", "[Payment terms]"] },
        ],
      },
      { t: "sign" },
    ],
  },
  {
    id: "sow",
    title: "Statement of Work",
    category: "Delivery",
    purpose: "Defines what will be delivered, by when, and who does what.",
    sections: [
      {
        h: "Background and objectives",
        blocks: [
          { t: "p", x: "[One short paragraph of context, then a bullet list of the objectives this work must achieve.]" },
        ],
      },
      {
        h: "Scope",
        blocks: [
          { t: "ul", items: ["In scope: [activities and deliverables included]", "Out of scope: [exclusions and boundaries]"] },
        ],
      },
      {
        h: "Phases",
        blocks: [
          { t: "table", head: ["Phase", "Activities", "Output", "Indicative timing"], rows: [["1. Discover", "[Discussions, data review]", "[Agreed scope note]", "[Week 1]"], ["2. Build", "[Preparation, modelling, drafting]", "[Draft deliverables]", "[Weeks 2–3]"], ["3. Review & handover", "[Walkthrough, refinements, docs]", "[Final deliverables]", "[Week 4]"]] },
        ],
      },
      {
        h: "Roles and responsibilities",
        blocks: [
          { t: "table", head: ["Person", "Role", "Responsibilities"], rows: [["Stephanie Madojemu", "Consultant", "[Analysis, build, documentation, handover]"], ["[Client contact]", "[Client lead]", "[Data access, reviews, decisions]"]] },
        ],
      },
      {
        h: "Change control",
        blocks: [
          { t: "p", x: "If the work proves larger than agreed, the consultant will flag it promptly. Any change to scope, price or timing is agreed in writing before the additional work continues." },
        ],
      },
      {
        h: "Acceptance",
        blocks: [
          { t: "ul", items: ["[Define what “done” means, e.g. dashboard reviewed in a walkthrough session and agreed corrections applied]"] },
        ],
      },
      { t: "sign" },
    ],
  },
  {
    id: "brd",
    title: "Business Requirements Document",
    category: "Business Analysis",
    purpose: "Documents what the business needs before a project or change begins.",
    sections: [
      {
        h: "Project overview",
        blocks: [
          { t: "fields", rows: [["Initiative", "[Name]"], ["Sponsor", "[Name and role]"], ["Author", "Stephanie Madojemu"], ["Status", "Draft for review"]] },
        ],
      },
      {
        h: "Business objectives",
        blocks: [
          { t: "ul", items: ["[Objective 1 — measurable where possible]", "[Objective 2]"] },
        ],
      },
      {
        h: "Current situation (AS-IS summary)",
        blocks: [
          { t: "p", x: "[Brief description of how things work today, referencing the AS-IS process map. Include pain points: delays, manual steps, gaps.]" },
        ],
      },
      {
        h: "Stakeholders",
        blocks: [
          { t: "table", head: ["Name", "Role", "Interest in this project"], rows: [["[Name]", "[Role]", "[e.g. owns the process; approves budget]"]] },
        ],
      },
      {
        h: "Business requirements",
        blocks: [
          { t: "ol", items: ["BR-01 — [What the business needs, in business language.]", "BR-02 — [Requirement.]"] },
        ],
      },
      {
        h: "Functional requirements",
        blocks: [
          { t: "ol", items: ["FR-01 — [What the system/solution must do to satisfy BR-01.]", "FR-02 — [Requirement.]"] },
        ],
      },
      {
        h: "Out of scope",
        blocks: [
          { t: "ul", items: ["[Explicit exclusions]"] },
        ],
      },
      {
        h: "Success criteria and open items",
        blocks: [
          { t: "ul", items: ["[How the change will be judged successful]", "Open items: [questions to resolve before build]"] },
        ],
      },
    ],
  },
  {
    id: "process-mapping",
    title: "AS-IS & TO-BE Process Mapping",
    category: "Business Analysis",
    purpose: "Captures a process as it works today and how it should work.",
    sections: [
      {
        h: "Process selected",
        blocks: [
          { t: "fields", rows: [["Process", "[e.g. order to invoice]"], ["Trigger", "[What starts the process]"], ["Start point", "[Where the map begins]"], ["End point", "[Where the map ends]"]] },
        ],
      },
      {
        h: "Roles / swimlanes",
        blocks: [
          { t: "ul", items: ["[Lane 1 — e.g. Customer]", "[Lane 2 — e.g. Sales]", "[Lane 3 — e.g. Operations]", "[Lane 4 — e.g. Finance]"] },
        ],
      },
      {
        h: "AS-IS map",
        blocks: [
          { t: "p", x: "[Insert or link the AS-IS map. List the steps in order underneath, one per lane.]" },
        ],
      },
      {
        h: "Observations",
        blocks: [
          { t: "table", head: ["Step", "Issue", "Impact"], rows: [["[Step ref]", "[e.g. manual re-keying between systems]", "[e.g. errors, ~2 hours lost per order]"], ["[Step ref]", "[e.g. approval waits 1–2 days]", "[e.g. orders queue with no visibility]"]] },
        ],
      },
      {
        h: "TO-BE map",
        blocks: [
          { t: "p", x: "[Insert or link the TO-BE map. Highlight what is automated, removed or merged, and confirm it with the people who do the work.]" },
        ],
      },
      {
        h: "Recommendations and metrics",
        blocks: [
          { t: "ul", items: ["[Improvement 1]", "[Improvement 2]", "Metrics to track after implementation: [e.g. cycle time, error rate, number of chase emails]"] },
        ],
      },
    ],
  },
  {
    id: "user-stories",
    title: "User Stories & Acceptance Criteria",
    category: "Business Analysis",
    purpose: "Developer- and client-ready stories with testable criteria.",
    sections: [
      {
        h: "How to use this template",
        blocks: [
          { t: "p", x: "One row per story. Keep stories small enough to test. Acceptance criteria use Given / When / Then so they can become UAT checks." },
        ],
      },
      {
        h: "Stories",
        blocks: [
          { t: "table", head: ["ID", "As a… I want… so that…", "Priority", "Acceptance criteria"], rows: [["US-01", "[As a sales team member, I want orders entered once, so that I do not re-key data.]", "Must", ["Given [context], when [action], then [result]."]], ["US-02", "[As a customer, I want to see my order status online, so that I do not chase by email.]", "Should", ["Given [context], when [action], then [result]."]]] },
        ],
      },
      {
        h: "Notes",
        blocks: [
          { t: "p", x: "[Assumptions, dependencies and any decisions made during refinement.]" },
        ],
      },
    ],
  },
  {
    id: "uat",
    title: "UAT Checklist",
    category: "Delivery",
    purpose: "Structures client testing before sign-off.",
    sections: [
      {
        h: "Test session",
        blocks: [
          { t: "fields", rows: [["Project", "[Name]"], ["Version / build", "[Ref]"], ["Tester", "[Name and role]"], ["Date", "[Date]"]] },
        ],
      },
      {
        h: "Checks",
        blocks: [
          { t: "table", head: ["Ref", "Requirement / story", "Expected result", "Pass / Fail", "Notes"], rows: [["UAT-01", "[US-01]", "[What should happen]", "[ ]", ""], ["UAT-02", "[US-02]", "[What should happen]", "[ ]", ""], ["UAT-03", "[Edge case]", "[What should happen]", "[ ]", ""]] },
        ],
      },
      {
        h: "Exit criteria",
        blocks: [
          { t: "ul", items: ["All “Must” stories pass.", "Any failed checks have an agreed fix or a documented workaround.", "[Other criteria agreed for this project]"] },
        ],
      },
      { t: "sign" },
    ],
  },
  {
    id: "handover",
    title: "Project Handover",
    category: "Delivery",
    purpose: "Records everything the client receives at the end of a project.",
    sections: [
      {
        h: "Summary",
        blocks: [
          { t: "p", x: "[One paragraph: what the project set out to do and what was delivered.]" },
        ],
      },
      {
        h: "Deliverables provided",
        blocks: [
          { t: "table", head: ["File / system", "Description", "Location"], rows: [["[Deliverable 1]", "[e.g. Power BI dashboard]", "[e.g. client workspace]"], ["[Deliverable 2]", "[e.g. documentation pack]", "[e.g. shared folder]"]] },
        ],
      },
      {
        h: "Systems and access used",
        blocks: [
          { t: "ul", items: ["[System — access used, licence ownership confirmed]"] },
        ],
      },
      {
        h: "Documentation",
        blocks: [
          { t: "ul", items: ["[What is documented where: data sources, refresh steps, how to update parameters, known limitations]"] },
        ],
      },
      {
        h: "Outstanding items and recommendations",
        blocks: [
          { t: "ul", items: ["[Anything intentionally left for later]", "[Recommended next steps]"] },
        ],
      },
      {
        h: "Support arrangements",
        blocks: [
          { t: "p", x: "[e.g. two weeks of email support included; ongoing support available via the monthly retainer.]" },
        ],
      },
      { t: "sign" },
    ],
  },
];

// --- simple HTML renderer so the sheet can be edited in-browser ---

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function renderHtml(t) {
  let h = "";
  h += `<header style="border-bottom:2px solid #563B78;padding-bottom:18px;margin-bottom:28px">`;
  h += `<p style="font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:#7D56AA;margin:0">${esc(t.category)}</p>`;
  h += `<h1 style="font-family:'Playfair Display',Georgia,serif;font-size:28px;color:#28272B;margin:10px 0 6px">${esc(t.title)}</h1>`;
  h += `<p style="color:#6D6B78;font-size:12px;margin:0">${esc(t.purpose)}</p></header>`;
  t.sections.forEach((sec) => {
    if (sec.h) h += `<h2 style="font-family:'Playfair Display',Georgia,serif;font-size:18px;color:#28272B;margin:26px 0 10px;border-bottom:1px solid #DED5EC;padding-bottom:6px">${esc(sec.h)}</h2>`;
    (sec.blocks || [sec]).forEach((b) => {
      if (b.t === "p") {
        h += `<p style="color:#44424c;font-size:13px;line-height:1.7;margin:8px 0">${esc(b.x)}</p>`;
      } else if (b.t === "ul" || b.t === "ol") {
        h += `<${b.t} style="color:#44424c;font-size:13px;line-height:1.8;margin:8px 0;padding-left:20px">`;
        b.items.forEach((i) => (h += `<li>${esc(i)}</li>`));
        h += `</${b.t}>`;
      } else if (b.t === "fields") {
        h += `<table style="border-collapse:collapse;width:100%;margin:8px 0">`;
        b.rows.forEach(([k, v]) => {
          h += `<tr><td style="border:1px solid #DED5EC;padding:8px 10px;font-size:12px;color:#7D56AA;width:32%">${esc(k)}</td><td style="border:1px solid #DED5EC;padding:8px 10px;font-size:13px;color:#44424c">${esc(v)}</td></tr>`;
        });
        h += `</table>`;
      } else if (b.t === "table") {
        h += `<table style="border-collapse:collapse;width:100%;margin:8px 0;font-size:12px">`;
        h += `<thead><tr>${b.head.map((c) => `<th style="border:1px solid #DED5EC;background:#EEE8FA;color:#563B78;padding:8px 10px;text-align:left">${esc(c)}</th>`).join("")}</tr></thead><tbody>`;
        b.rows.forEach((r) => {
          h += `<tr>${r.map((c) => `<td style="border:1px solid #DED5EC;padding:8px 10px;color:#44424c;vertical-align:top">${Array.isArray(c) ? c.map(esc).join("<br/>") : esc(c)}</td>`).join("")}</tr>`;
        });
        h += `</tbody></table>`;
      } else if (b.t === "sign") {
        h += `<table style="width:100%;margin-top:34px;font-size:12px;color:#44424c"><tr><td style="border-top:1px solid #28272B;width:40%;padding-top:6px">Signed (consultant) &amp; date</td><td style="width:10%"></td><td style="border-top:1px solid #28272B;width:40%;padding-top:6px">Signed (client) &amp; date</td></tr></table>`;
      }
    });
  });
  h += `<p style="margin-top:36px;border-top:1px solid #DED5EC;padding-top:10px;font-size:10px;color:#6D6B78">Savvy Stephanie Analytics &amp; Consulting · Stephanie Madojemu · savvystephanie1@gmail.com · Private business template</p>`;
  return h;
}

function TemplateDoc({ template, resetFlag }) {
  const storageKey = `ss_tpl_${template.id}`;
  const html = useMemo(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && !resetFlag) return saved;
    } catch {}
    return renderHtml(template);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, resetFlag]);
  const timer = useRef(null);
  return (
    <div
      key={`${template.id}-${resetFlag}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      data-testid="template-sheet"
      className="doc-sheet print-area mx-auto max-w-[820px] rounded-2xl border border-line p-8 sm:p-12"
      dangerouslySetInnerHTML={{ __html: html }}
      onInput={(e) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          try {
            localStorage.setItem(storageKey, e.currentTarget.innerHTML);
          } catch {}
        }, 600);
      }}
    />
  );
}

export default function Templates() {
  const [activeId, setActiveId] = useState(templates[0].id);
  const [resetFlag, setResetFlag] = useState(0);
  const active = templates.find((t) => t.id === activeId);
  return (
    <div>
      <div className="no-print flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted">
          Eight matching business document templates with sample text and placeholders. Edit any text
          directly in the preview, then print or save as PDF.
        </p>
        <div className="flex gap-3">
          <button
            data-testid="template-reset-button"
            onClick={() => {
              try {
                localStorage.removeItem(`ss_tpl_${active.id}`);
              } catch {}
              setResetFlag((f) => f + 1);
            }}
            className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-plum hover:text-plum"
          >
            Reset edits
          </button>
          <button
            data-testid="template-print-button"
            onClick={() => window.print()}
            className="rounded-full bg-plum px-5 py-2 text-xs font-semibold text-white hover:bg-ink"
          >
            Print / Save as PDF
          </button>
        </div>
      </div>
      <div className="no-print mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Document templates">
        {templates.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeId === t.id}
            data-testid={`template-tab-${t.id}`}
            onClick={() => setActiveId(t.id)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
              activeId === t.id ? "bg-plum text-white" : "border border-line bg-white text-ink hover:border-plum hover:text-plum"
            }`}
          >
            {t.title}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <TemplateDoc template={active} resetFlag={resetFlag} />
      </div>
    </div>
  );
}
